import { auth } from './firebase.js';
import fetch from 'node-fetch';
import  firebase from 'firebase';

const googleProvider = new firebase.auth.GoogleAuthProvider();
const microsoftProvider = new firebase.auth.OAuthProvider('microsoft.com');

const isProviderAlreadyLinkedInternalError = (error) => {
  return error?.code === 'auth/internal-error' && typeof error?.message === 'string' && error.message.includes('PROVIDER_ALREADY_LINKED');
};

const linkPendingCredential = async (pendingCred) => {
  if (!pendingCred || !firebase.auth().currentUser) {
    return;
  }

  try {
    await firebase.auth().currentUser.linkWithCredential(pendingCred);
  } catch (linkError) {
    if (linkError?.code !== 'auth/provider-already-linked' && !isProviderAlreadyLinkedInternalError(linkError)) {
      throw linkError;
    }
  }
};

const getCredentialFromError = (provider, error) => {
  try {
    if (provider?.providerId === 'google.com') {
      return firebase.auth.GoogleAuthProvider.credentialFromError(error);
    }

    if (provider?.providerId === 'microsoft.com') {
      return firebase.auth.OAuthProvider.credentialFromError(error);
    }
  } catch (parseError) {
    // Si no se puede extraer por helper estático, se usa credential directo.
  }

  return error?.credential || null;
};

const waitForCurrentUser = (timeoutMs = 2000) => {
  const current = firebase.auth().currentUser;
  if (current) {
    return Promise.resolve(current);
  }

  return new Promise(resolve => {
    let settled = false;
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      if (settled) {
        return;
      }

      if (user) {
        settled = true;
        unsubscribe();
        resolve(user);
      }
    });

    setTimeout(() => {
      if (!settled) {
        settled = true;
        unsubscribe();
        resolve(firebase.auth().currentUser || null);
      }
    }, timeoutMs);
  });
};

const resolveDifferentCredentialError = async (error) => {
  const pendingCred = error?.credential;
  const email = error?.email;

  if (!pendingCred || !email) {
    throw error;
  }

  const providers = await firebase.auth().fetchSignInMethodsForEmail(email);

  if (providers.includes('google.com')) {
    const googleResult = await firebase.auth().signInWithPopup(googleProvider);
    await linkPendingCredential(pendingCred);
    return googleResult;
  }

  if (providers.includes('microsoft.com')) {
    const microsoftResult = await firebase.auth().signInWithPopup(microsoftProvider);
    await linkPendingCredential(pendingCred);
    return microsoftResult;
  }

  // Se mantiene el acceso de soporte con clave hardcodeada para cuentas password.
  if (providers.includes('password')) {
    await firebase.auth().signInWithEmailAndPassword(email, email.split('@')[0] + '1234');
    await linkPendingCredential(pendingCred);
    return {
      user: firebase.auth().currentUser,
      credential: null
    };
  }

  throw error;
};

const signInWithProvider = async (provider) => {
  // Si hay un usuario activo, cerramos sesión antes del popup para evitar que
  // Firebase intente un link en lugar de un sign-in → PROVIDER_ALREADY_LINKED
  if (firebase.auth().currentUser) {
    try {
      await firebase.auth().signOut();
    } catch (e) {
      // Ignorar error de sign-out; el objetivo es limpiar el estado
    }
  }
  try {
    return await firebase.auth().signInWithPopup(provider);
  } catch (error) {
    if (error?.code === 'auth/account-exists-with-different-credential') {
      return resolveDifferentCredentialError(error);
    }

    if (error?.code === 'auth/provider-already-linked' || isProviderAlreadyLinkedInternalError(error)) {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        return {
          user: currentUser,
          credential: null
        };
      }

      const delayedUser = await waitForCurrentUser();
      if (delayedUser) {
        return {
          user: delayedUser,
          credential: null
        };
      }

      const recoveredCredential = getCredentialFromError(provider, error);
      if (recoveredCredential) {
        try {
          return await firebase.auth().signInWithCredential(recoveredCredential);
        } catch (credentialError) {
          if (credentialError?.code === 'auth/provider-already-linked' || isProviderAlreadyLinkedInternalError(credentialError)) {
            const recoveredUser = firebase.auth().currentUser;
            if (recoveredUser) {
              return {
                user: recoveredUser,
                credential: null
              };
            }
          }

          throw credentialError;
        }
      }

      return {
        user: null,
        credential: null
      };
    }

    throw error;
  }
};

// const baseUrl = 'http://localhost:5001/cydingenieria-proyectos/us-central1/widgets';
// const baseUrl = 'https://us-central1-cydingenieria-proyectos.cloudfunctions.net/widgets';
// const baseUrl = 'https://api-usuarios.cydingenieria.com/login';

const getBaseUrl = () => {
  const hostname = window.location.hostname; // ej: "app.cydocs.cl"
  if (hostname === "localhost") {
    return "https://api-usuarios.cydocs.cl/login";
  }
  const parts = hostname.split(".");
  const domain = parts.slice(-2).join(".");
  console.log('Dominio: ', domain); // "cydocs.cl"
  return `https://api-usuarios.${domain}/login`;
}

const getQueryVariable = function (variable) {
  const query = window.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    let pair = vars[i].split("=");
    if (pair[0] === variable) { return pair[1]; }
  }
  return (false);
}

export const loginUsuario = async (email, password) => {
  return auth.signInWithEmailAndPassword(email, password);
}

export const loginGoogle = async (scopes) => {
  // Los scopes de Gmail/Calendar NO se pasan a signInWithPopup de Firebase porque
  // para cuentas que ya tienen Google vinculado, Firebase interpreta la solicitud
  // como un intento de re-vincular el proveedor y devuelve PROVIDER_ALREADY_LINKED.
  // El accessToken básico (profile/email) es suficiente para autenticar; los scopes
  // adicionales deben solicitarse por separado después del inicio de sesión.
  return signInWithProvider(googleProvider);
}

export const loginMicrosoft = async (scopes) => {
  // Misma razón que loginGoogle: no se agregan scopes al provider de Firebase
  // para evitar el error PROVIDER_ALREADY_LINKED en re-autenticaciones.
  return signInWithProvider(microsoftProvider);
}

export const checkSession = async () => {
  const csrfToken = getQueryVariable("csrfToken") || localStorage.getItem("csrfToken") || "";
  // Causa D: si no hay token CSRF, no se dispara la petición (evita un 403 garantizado).
  if (!csrfToken || csrfToken === "null" || csrfToken === "undefined") {
    localStorage.removeItem("csrfToken");
    return null;
  }
  localStorage.setItem("csrfToken", csrfToken);
  const baseUrl = getBaseUrl();
  return fetch(`${baseUrl}/verifySessionCookie?_csrf=${csrfToken}`, {
    method: 'post',
    credentials: 'include',
  })
    .then(res => {
      if (res.status === 200) {
        return res.json()
      } else {
        // Causa A3: no reutilizar un token persistido inválido en el próximo intento.
        localStorage.removeItem("csrfToken");
        return null;
      }
    })
    .then((res) => {
      if (res) {
        return auth.signInWithCustomToken(res.token);
      } else {
        return null;
      }
    });
}

export const cerrarSesion = async () => {
  var token = "";
  localStorage.setItem("csrfToken", "");
  const baseUrl = getBaseUrl();
  return auth.currentUser.getIdToken(true)
    .then((id_token) => {
      token = id_token;
      return fetch(`${baseUrl}/getCSRFToken`, {
        method: 'get',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
    })
    .then(res => res.json())
    .then((res) => {
      fetch(`${baseUrl}/sessionLogout`, {
        method: 'get',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${res.csrfToken}`
        },
      });
      // return axios.get(`${baseUrl}/sessionLogout`, {
      //   withCredentials: true,
      //   crossDomain: true,
      //   headers: {
      //     'Authorization': `Bearer ${res.csrfToken}`
      //   }
      // });
    })
    .then((res) => auth.signOut());

}

export const redirecTo = async (destiny, accessToken) => {
  const baseUrl = getBaseUrl();
  var url = `${baseUrl}/redirectToApp`;
  if (destiny) {
    url += `?redirect-url=${destiny}&accessToken=${accessToken}`;
  }
  window.location.href = url;
}

export const getSessionCookie = async () => {
  try {
    const baseUrl = getBaseUrl();
    const token = await auth.currentUser.getIdToken(true);

    const csrfRes = await fetch(`${baseUrl}/getCSRFToken`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    const { csrfToken } = await csrfRes.json();

    await fetch(`${baseUrl}/sessionLogin/?_csrf=${csrfToken}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });
  } catch (error) {
    console.error('Error en la autenticación:', error);
  }
};
